/**
 * Message interface for project context
 * Represents a single message in the context history
 * 
 * @property id - Unique identifier for the message
 * @property content - Text content of the message
 * @property role - Who sent the message (user or assistant)
 * @property timestamp - When the message was sent (milliseconds since epoch)
 */
export interface Message {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: number;
}

/**
 * Project interface for use with React Query
 * 
 * Core project data received from the server with client-side computed properties
 * This is the main interface used throughout the application for project data.
 * 
 * The interface contains both server-provided properties (like id, name) and
 * client-computed properties (like isOwner, isShared) that are derived from
 * the raw data but useful for the UI.
 * 
 * @property id - Unique identifier for the project
 * @property name - Display name of the project
 * @property archived - Whether the project is archived
 * @property description - Optional project description
 * @property context - Optional project context information
 * @property avatar_url - URL to the project avatar image
 * @property avatar_updated_at - When the avatar was last updated
 * @property last_selected_at - When the project was last viewed
 * @property user_id - ID of the project owner
 * @property created_at - When the project was created
 * @property updated_at - When the project was last updated
 * @property messages - Array of context messages for the project
 * @property lastUpdated - Client-side timestamp of last update
 * @property isOwner - Whether current user owns this project
 * @property isShared - Whether project is shared with others
 */
export interface Project {
  // Required properties from server
  id: string;
  name: string;
  archived: boolean;
  
  // Optional properties from server
  description: string;
  context: string;
  avatar_url?: string | null;
  avatar_updated_at?: string | null;
  last_selected_at?: string | null;
  user_id?: string;
  created_at?: string;
  updated_at?: string;
  
  // Client-side computed properties
  messages: Message[];
  lastUpdated: number;
  isOwner: boolean;
  isShared: boolean;
}

/**
 * Project data as stored in the database
 * Represents the raw server structure before client-side processing
 * 
 * This interface closely matches the database schema and contains only
 * the properties that come directly from the server. It's converted to
 * the Project interface for use in the application.
 * 
 * @property id - Unique identifier for the project
 * @property name - Display name of the project
 * @property description - Optional project description
 * @property context - Optional project context information
 * @property avatar_url - URL to the project avatar image
 * @property avatar_updated_at - When the avatar was last updated
 * @property user_id - ID of the project owner
 * @property created_at - When the project was created
 * @property updated_at - When the project was last updated
 * @property last_selected_at - When the project was last viewed
 * @property archived - Whether the project is archived
 * @property metadata - Additional metadata about the project
 */
export interface ProjectData {
  id: string;
  name: string;
  description?: string | null;
  context?: string | null;
  avatar_url?: string | null;
  avatar_updated_at?: string | null;
  user_id: string;
  created_at: string;
  updated_at: string;
  last_selected_at?: string | null;
  archived: boolean;
  metadata?: {
    client_id?: string;
    [key: string]: any;
  };
}

/**
 * Input type for creating a new project
 * Contains the minimum required fields to create a new project
 * 
 * @property id - Unique identifier for the new project (client-generated)
 * @property name - Display name of the project
 * @property description - Optional project description
 * @property context - Optional project context information
 * @property avatarFile - Optional image file for the project avatar
 */
export type CreateProjectInput = {
  id: string;
  name: string;
  description?: string;
  context?: string;
  avatarFile?: File; // Add avatarFile to the CreateProjectInput type
};

/**
 * Input type for updating a project
 * Partial type allowing any subset of project properties to be updated
 * 
 * @property name - Updated project name
 * @property description - Updated project description
 * @property context - Updated project context
 * @property avatar_url - Updated avatar URL
 */
export type UpdateProjectInput = Partial<{
  name: string;
  description: string;
  context: string;
  avatar_url: string;
}>;

/**
 * Input for avatar upload operation
 * Parameters required for uploading a project avatar
 * 
 * @property projectId - ID of the project to update
 * @property file - File object containing the image data
 */
export interface ProjectAvatarUploadInput {
  projectId: string;
  file: File;
}

/**
 * Response structure for shared status API call
 * Map of project IDs to boolean indicating if they're shared
 * 
 * @example
 * {
 *   "project-123": true,
 *   "project-456": false
 * }
 */
export interface SharedStatusResponse {
  [key: string]: boolean;
}

/**
 * Utility function to format a project from server data
 * Converts database representation to application representation
 * 
 * This function takes the raw data from the server and transforms it into
 * the format expected by the application, including computing derived properties
 * like isOwner and ensuring consistent defaults for optional fields.
 * 
 * @param data - Raw project data from the database
 * @param currentUserId - ID of the current user for ownership check
 * @returns Formatted project object with computed properties
 * 
 * @example
 * // Transform server data into application format
 * const project = formatProject(serverData, userId);
 */
export function formatProject(data: ProjectData, currentUserId: string): Project {
  return {
    id: data.id,
    name: data.name,
    description: data.description || '',
    context: data.context || '',
    avatar_url: data.avatar_url,
    avatar_updated_at: data.avatar_updated_at,
    lastUpdated: new Date(data.updated_at).getTime(),
    last_selected_at: data.last_selected_at,
    archived: data.archived,
    isOwner: String(data.user_id) === String(currentUserId),
    isShared: false, // Default value, will be updated later for owned projects
    messages: [],
    user_id: data.user_id,
    created_at: data.created_at,
    updated_at: data.updated_at,
  };
}